import { INPUT_TYPES } from '../../../shared/tabbed-table/constants';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { UtilService } from '@service/util/util.service';

export const LOAD_TAB_TYPES = {
	ACTIVE: 'Active',
	OPEN: 'Open',
	BOOKED: 'Booked',
};

export const LOAD_FIELDS = {
	LOAD_NUMBER: 'Load Number',
	CUSTOMER: 'Customer',
	CARRIER: 'Carrier',
	PICKUP_DATE: 'Pickup Date',
	DELIVERY_DATE: 'Delivery Date',
	ORIGIN: 'Origin',
	DESTINATION: 'Destination',
	EQUIPMENT: 'Equipment',
	ALL_IN_COST: 'All In Cost',
	PRICE_TO_SHIPPER: 'Price To Shipper',
	MATCHED_CARRIERS: 'Matched Carriers',
	CARRIERS_CONTACTED: 'Carriers Contacted',
	VIEWS: 'Views',
	CARRIER_OFFERS: 'Carrier Offers',
	LOAD_STATUS: 'Load Status',
	USER: 'User',
	PREDICTED_PRICE: 'Predicted Price',
	ORIGIN_CITY: 'Origin City',
	ORIGIN_STATE: 'Origin State',
	DESTINATION_CITY: 'Destination City',
	DESTINATION_STATE: 'Destination State',
	PROJECT: 'Project',
};

export const SORT_FIELD_MAPS = {
	[LOAD_FIELDS.LOAD_NUMBER]: 'referenceNumber',
	[LOAD_FIELDS.CUSTOMER]: 'customer',
	[LOAD_FIELDS.PICKUP_DATE]: 'pickupDate',
	[LOAD_FIELDS.DELIVERY_DATE]: 'deliveryDate',
	[LOAD_FIELDS.ORIGIN]: 'origin',
	[LOAD_FIELDS.DESTINATION]: 'destination',
	[LOAD_FIELDS.ORIGIN_CITY]: 'originCity',
	[LOAD_FIELDS.ORIGIN_STATE]: 'originState',
	[LOAD_FIELDS.DESTINATION_CITY]: 'destinationCity',
	[LOAD_FIELDS.DESTINATION_STATE]: 'destinationState',
	[LOAD_FIELDS.EQUIPMENT]: 'equipment',
	[LOAD_FIELDS.ALL_IN_COST]: 'allInCost',
	[LOAD_FIELDS.PRICE_TO_SHIPPER]: 'priceToShipper',
	[LOAD_FIELDS.CARRIERS_CONTACTED]: 'offersCount',
	[LOAD_FIELDS.VIEWS]: 'viewsCount',
	[LOAD_FIELDS.CARRIER_OFFERS]: 'bidCount',
	[LOAD_FIELDS.LOAD_STATUS]: 'loadStatusText',
	[LOAD_FIELDS.USER]: 'user',
};

export let filterFieldsInputTypeMap: Map<string, string> = new Map<string, string>();

filterFieldsInputTypeMap.set(LOAD_FIELDS.LOAD_NUMBER, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(LOAD_FIELDS.CUSTOMER, INPUT_TYPES.CUSTOMER);
filterFieldsInputTypeMap.set(LOAD_FIELDS.PICKUP_DATE, INPUT_TYPES.DATE);
filterFieldsInputTypeMap.set(LOAD_FIELDS.DELIVERY_DATE, INPUT_TYPES.DATE);

filterFieldsInputTypeMap.set(LOAD_FIELDS.ORIGIN_CITY, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(LOAD_FIELDS.ORIGIN_STATE, INPUT_TYPES.STATE);
filterFieldsInputTypeMap.set(LOAD_FIELDS.DESTINATION_CITY, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(LOAD_FIELDS.DESTINATION_STATE, INPUT_TYPES.STATE);

filterFieldsInputTypeMap.set(LOAD_FIELDS.EQUIPMENT, INPUT_TYPES.TRAILER_TYPE);
filterFieldsInputTypeMap.set(LOAD_FIELDS.ALL_IN_COST, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(LOAD_FIELDS.PRICE_TO_SHIPPER, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(LOAD_FIELDS.USER, INPUT_TYPES.USERS_LIST);
filterFieldsInputTypeMap.set(LOAD_FIELDS.CARRIER, INPUT_TYPES.CARRIER);
filterFieldsInputTypeMap.set(LOAD_FIELDS.PROJECT, INPUT_TYPES.PROJECT);

export function buildHeaders(
	accountSettings: AccountSettingsService,
	isHistoricalLoadTab: boolean = false,
	isCustomTabOpen: boolean = false,
	selectedLoadType: string
) {
	if (!isHistoricalLoadTab) {
		const isActiveTab = selectedLoadType === LOAD_TAB_TYPES.ACTIVE;
		let headers = [
			{ dataName: 'select', formattedName: 'select' },
			{ dataName: 'referenceNumber', formattedName: LOAD_FIELDS.LOAD_NUMBER },
			{ dataName: 'pickupDate', formattedName: LOAD_FIELDS.PICKUP_DATE },
			{ dataName: 'deliveryDate', formattedName: LOAD_FIELDS.DELIVERY_DATE },
			{ dataName: 'origin', formattedName: LOAD_FIELDS.ORIGIN },
			{ dataName: 'destination', formattedName: LOAD_FIELDS.DESTINATION },
			{ dataName: 'trailerType', formattedName: LOAD_FIELDS.EQUIPMENT },
			{ dataName: 'price', formattedName: LOAD_FIELDS.ALL_IN_COST },
			{
				dataName: isActiveTab ? 'matchedCarriersCount' : 'priceToShipper',
				formattedName: isActiveTab ? LOAD_FIELDS.MATCHED_CARRIERS : LOAD_FIELDS.PRICE_TO_SHIPPER,
			},
			{ dataName: 'offersCount', formattedName: LOAD_FIELDS.CARRIERS_CONTACTED },
			{ dataName: 'viewsCount', formattedName: LOAD_FIELDS.VIEWS },
			{ dataName: 'bidCount', formattedName: LOAD_FIELDS.CARRIER_OFFERS },
			{ dataName: 'loadStatusText', formattedName: LOAD_FIELDS.LOAD_STATUS },
			{ dataName: 'userName', formattedName: LOAD_FIELDS.USER },
		];
		if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
			headers.splice(2, 0, { dataName: 'customerName', formattedName: LOAD_FIELDS.CUSTOMER });
		}
		if (isCustomTabOpen) {
			headers.splice(3, 0, { dataName: 'carrierName', formattedName: LOAD_FIELDS.CARRIER });
		}
		return headers;
	} else {
		let headers = [
			{ dataName: 'select', formattedName: 'select' },
			{ dataName: 'referenceNumber', formattedName: LOAD_FIELDS.LOAD_NUMBER },
			{ dataName: 'carrierName', formattedName: LOAD_FIELDS.CARRIER },
			{ dataName: 'pickupDate', formattedName: LOAD_FIELDS.PICKUP_DATE },
			{ dataName: 'deliveryDate', formattedName: LOAD_FIELDS.DELIVERY_DATE },
			{ dataName: 'origin', formattedName: LOAD_FIELDS.ORIGIN },
			{ dataName: 'destination', formattedName: LOAD_FIELDS.DESTINATION },
			{ dataName: 'trailerType', formattedName: LOAD_FIELDS.EQUIPMENT },
			{ dataName: 'price', formattedName: LOAD_FIELDS.ALL_IN_COST },
			{ dataName: 'priceToShipper', formattedName: LOAD_FIELDS.PRICE_TO_SHIPPER },
			{ dataName: 'bidCount', formattedName: LOAD_FIELDS.CARRIER_OFFERS },
			{ dataName: 'predictedPrice', formattedName: LOAD_FIELDS.PREDICTED_PRICE },
		];
		if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
			headers.splice(1, 0, { dataName: 'customerName', formattedName: LOAD_FIELDS.CUSTOMER });
		}
		return headers;
	}
}

export function buildHeaderDataTypes(
	utilService: UtilService,
	accountSettings: AccountSettingsService,
	isHistoricalLoadTab: boolean = false,
	isCustomTabOpen: boolean = false,
	selectedLoadType: string
) {
	const isActiveTab = selectedLoadType === LOAD_TAB_TYPES.ACTIVE;
	const hidePriceToShipperColumn =
		!utilService.isUserRoleAdmin() &&
		accountSettings.HIDE_CUSTOMER_DETAILS_FROM_NON_ADMIN_USERS &&
		!isActiveTab;
	if (!isHistoricalLoadTab) {
		let priceToShipperColumnIndex = 8;

		let types = [
			'string',
			'date',
			'date',
			'string',
			'string',
			'string',
			'currency',
			isActiveTab ? 'number' : 'currency',
			'number',
			'number',
			'number',
			'string',
			'string',
		];
		if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
			types.splice(1, 0, 'string');
			priceToShipperColumnIndex += 1;
		}
		if (isCustomTabOpen) {
			types.splice(2, 0, 'string');
			priceToShipperColumnIndex += 1;
		}
		if (hidePriceToShipperColumn) {
			types.splice(priceToShipperColumnIndex, 1);
		}
		return types;
	} else {
		let priceToShipperColumnIndex = 9;
		let types = [
			'string',
			'string',
			'date',
			'date',
			'string',
			'string',
			'string',
			'currency',
			'currency',
			'number',
			'currency',
		];
		if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
			types.splice(1, 0, 'string');
			priceToShipperColumnIndex += 1;
		}
		if (hidePriceToShipperColumn) {
			types.splice(priceToShipperColumnIndex, 1);
		}
		return types;
	}
}

export function buildDisplayedColumns(
	utilService: UtilService,
	accountSettings: AccountSettingsService,
	isHistoricalLoadTab: boolean = false,
	isCustomTabOpen: boolean = false,
	selectedLoadType: string
): string[] {
	let columns = [];
	const priceToShipperColumnName = 'priceToShipper',
		matchedCarriersCountName = 'matchedCarriersCount';
	const hidePriceToShipperColumn =
		!utilService.isUserRoleAdmin() &&
		accountSettings.HIDE_CUSTOMER_DETAILS_FROM_NON_ADMIN_USERS &&
		selectedLoadType !== LOAD_TAB_TYPES.ACTIVE;
	if (!isHistoricalLoadTab) {
		const isActiveTab = selectedLoadType === LOAD_TAB_TYPES.ACTIVE;
		columns = [
			'select',
			'referenceNumber',
			'pickupDate',
			'deliveryDate',
			'origin',
			'destination',
			'trailerType',
			'price',
			isActiveTab ? matchedCarriersCountName : priceToShipperColumnName,
			'offersCount',
			'viewsCount',
			'bidCount',
			'loadStatusText',
			'userName',
		];
		if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
			columns.splice(2, 0, 'customerName');
		}
		if (isCustomTabOpen) {
			columns.splice(3, 0, 'carrierName');
		}
	} else {
		columns = [
			'select',
			'referenceNumber',
			'carrierName',
			'pickupDate',
			'deliveryDate',
			'origin',
			'destination',
			'trailerType',
			'price',
			priceToShipperColumnName,
			'bidCount',
			'predictedPrice',
		];
		if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
			columns.splice(2, 0, 'customerName');
		}
	}
	if (hidePriceToShipperColumn) {
		const priceToShipperIndex = columns.indexOf(priceToShipperColumnName);
		columns.splice(priceToShipperIndex, 1);
	}
	return columns;
}

export function buildSortFields() {
	return [
		LOAD_FIELDS.PICKUP_DATE,
		LOAD_FIELDS.DELIVERY_DATE,
		LOAD_FIELDS.ORIGIN_CITY,
		LOAD_FIELDS.ORIGIN_STATE,
		LOAD_FIELDS.DESTINATION_CITY,
		LOAD_FIELDS.DESTINATION_STATE,
		LOAD_FIELDS.EQUIPMENT,
		LOAD_FIELDS.ALL_IN_COST,
	];
}

export function buildFilerFields(
	accountSettings: AccountSettingsService,
	isUserRoleAdmin: boolean,
	isHistoricalLoadTab: boolean = false
) {
	let filters = [
		LOAD_FIELDS.LOAD_NUMBER,
		LOAD_FIELDS.PICKUP_DATE,
		LOAD_FIELDS.DELIVERY_DATE,
		LOAD_FIELDS.ORIGIN_CITY,
		LOAD_FIELDS.ORIGIN_STATE,
		LOAD_FIELDS.DESTINATION_CITY,
		LOAD_FIELDS.DESTINATION_STATE,
		LOAD_FIELDS.EQUIPMENT,
		LOAD_FIELDS.CARRIER,
		LOAD_FIELDS.ALL_IN_COST,
		LOAD_FIELDS.USER,
	];
	if (accountSettings.NON_ADMIN_SEE_OWN_LOADS_ONLY && !isUserRoleAdmin) {
		filters.pop();
	}
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		filters.splice(1, 0, LOAD_FIELDS.CUSTOMER);
	}
	if (!isHistoricalLoadTab) {
		filters.push(LOAD_FIELDS.PROJECT);
	}
	return filters;
}
